import React, { useContext, useEffect, useState } from 'react'
import classNames from 'classnames/bind'
import PropTypes from 'prop-types'

import styles from './CartItem.module.scss'
import { changeQuantityItemCart, priceTotalItemCart, removeItemCart } from '~/redux/cartSlice'
import { Box, Typography } from '@mui/material'
import { convertPrice } from '~/utils'
import { imageMissing } from '~/assets/images'
import { FoodItemTopping, ToolTip } from '~/components'
import { MinusCircleIcon, PlusCircleIcon, XMarkIcon } from '~/assets/icons'
import { ThemeContext } from '@emotion/react'
import {
  MAX_QUANTITY_CART,
  MIN_QUANTITY_CART,
  NUMBER_DECREASE,
  NUMBER_INCREMENT,
} from '~/views/YourCart/components/CartItem'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

const cx = classNames.bind(styles)

// Cart item trên desktop
const CartItem = ({
  name,
  price,
  original_price = 0,
  discount_amount = 0,
  note,
  image,
  quantity,
  foodOptionTypes = [],
  primaryKey,
  stock_quantity,
  setErrorItemCart,
  max_quantity_per_order,
  setMessErr,
  isHidePlusEnabled,
}) => {
  const [currentQuantity, setCurrentQuantity] = useState(quantity)
  const [open, setOpen] = useState(false)
  const [messageErr, setMessageErr] = useState('')

  const { t } = useTranslation()
  const { currentThemeObject } = useContext(ThemeContext)
  const dispatch = useDispatch()
  const isHidePlusFoodItem = isHidePlusEnabled && price === 0

  useEffect(() => {
    if (currentQuantity > stock_quantity && stock_quantity) {
      setMessageErr(t('errors.errorQuantityExceeded'))
      setErrorItemCart(true)
      return
    }
    if (currentQuantity > max_quantity_per_order && max_quantity_per_order) {
      setMessageErr(t('errors.errorQuantityPerOrder'))
      setErrorItemCart(true)
      return
    }
    setMessageErr('')
    setErrorItemCart(false)
  }, [currentQuantity, stock_quantity, max_quantity_per_order])

  const openToolTip = () => {
    setOpen(true)
  }

  const closeToolTip = () => {
    setOpen(false)
  }

  const changeQuantityHandler = (numQuantity) => {
    setMessErr(false)
    if (currentQuantity <= MIN_QUANTITY_CART && numQuantity === NUMBER_DECREASE) {
      openToolTip()
      return
    }

    if (currentQuantity >= MAX_QUANTITY_CART && numQuantity === NUMBER_INCREMENT) return

    setCurrentQuantity((prevQuantity) => {
      return prevQuantity + numQuantity
    })

    dispatch(changeQuantityItemCart({ primaryKey, numQuantity }))
  }

  const removeItemCartHandler = () => {
    dispatch(removeItemCart(primaryKey))
  }

  const renderOriginalPrice = () => {
    if (!discount_amount) return null

    const originalPrice = priceTotalItemCart({
      price: original_price,
      foodOptionTypes,
    })

    return (
      <Box
        lineHeight="1.2"
        fontSize="1.2rem"
        fontWeight="400"
        color="#000"
        sx={{ textDecoration: 'line-through' }}>
        {convertPrice(originalPrice)}
      </Box>
    )
  }

  const renderPromotionalPrice = () => {
    const promotionalPrice = priceTotalItemCart({
      price,
      foodOptionTypes,
    })

    return (
      <Box lineHeight="1.4" fontSize="1.4rem" fontWeight="600" color="#ff8f1f">
        {convertPrice(promotionalPrice)}
      </Box>
    )
  }

  const renderContentTooltip = () => {
    return (
      <Box display="flex" alignItems="center">
        <Typography
          onClick={removeItemCartHandler}
          fontWeight="600"
          padding="12px"
          fontSize="1.6rem"
          component="div"
          color="red">
          {t('actions.delete_dish')}
        </Typography>
        <Typography padding="12px" onClick={closeToolTip}>
          <XMarkIcon size="12" />
        </Typography>
      </Box>
    )
  }

  const renderActionItemCart = () => {
    return (
      <div className={cx('action')}>
        <ToolTip open={open} contentToolTip={renderContentTooltip()} onClose={closeToolTip}>
          <Typography
            lineHeight="0"
            paddingY="10px"
            paddingRight="8px"
            onClick={() => changeQuantityHandler(NUMBER_DECREASE)}>
            <MinusCircleIcon color={currentThemeObject?.colorText1} />
          </Typography>
        </ToolTip>
        <p className={cx('quantity')}>{quantity}</p>
        <Typography
          lineHeight="0"
          paddingY="8px"
          paddingLeft="8px"
          visibility={isHidePlusFoodItem ? 'hidden' : 'visible'}
          onClick={() => changeQuantityHandler(NUMBER_INCREMENT)}>
          <PlusCircleIcon color={currentThemeObject?.colorText1} />
        </Typography>
      </div>
    )
  }

  const renderDetailItemCart = () => {
    return (
      <div className={cx('content_item_cart')}>
        <img
          width={70}
          height={70}
          src={image?.url || imageMissing}
          className={cx('img_food')}
          alt=""
        />
        <div className={cx('detail_item_cart')}>
          <div className={cx('detail_food')}>
            <p className={cx('item_name')}>{name}</p>
            <div className={cx('detail_price')}>
              {renderPromotionalPrice()}
              {renderOriginalPrice()}
            </div>
            <FoodItemTopping
              foodOptionTypes={foodOptionTypes || []}
              color={'baseColor.colorText1'}
            />
            <Typography fontSize="1.4rem" fontStyle="italic" color={'baseColor.colorText1'}>
              {note}
            </Typography>
          </div>
          {renderActionItemCart()}
        </div>
      </div>
    )
  }

  return (
    <div className={cx('item_cart')}>
      <div className={cx('form_item_cart')}>{renderDetailItemCart()}</div>
      <p className={cx('error_quantity')}>{messageErr}</p>
    </div>
  )
}

export default React.memo(CartItem)

CartItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  price: PropTypes.number,
  original_price: PropTypes.number,
  discount_amount: PropTypes.number,
  foodOptionTypes: PropTypes.array,
  note: PropTypes.string,
  image: PropTypes.object,
  quantity: PropTypes.number,
  primaryKey: PropTypes.string,
  stock_quantity: PropTypes.number,
  setErrorItemCart: PropTypes.func,
  max_quantity_per_order: PropTypes.number,
  setMessErr: PropTypes.func,
  isHidePlusEnabled: PropTypes.bool,
}
